/** Pure, best-effort classification of synchronous browser facts. */
export function buildTransactionMetadata(facts) {
  const ua = typeof facts.userAgent === "string" ? facts.userAgent : "";
  const platform = typeof facts.platform === "string" ? facts.platform : "";
  const signals = `${ua} ${platform}`;
  const isIPad =
    /iPad/i.test(signals) ||
    (/Macintosh|MacIntel/i.test(signals) && (facts.maxTouchPoints || 0) > 1);
  const isIOS = isIPad || /iPhone|iPod/i.test(signals);
  const isAndroid = /Android/i.test(ua);
  const isTablet =
    isIPad ||
    /Tablet|PlayBook|Silk/i.test(ua) ||
    (isAndroid && !/Mobile/i.test(ua));
  const isMobile = /Mobi|iPhone|iPod/i.test(signals) || isAndroid;
  const isDesktop =
    /Windows NT|Win32|Win64|Macintosh|MacIntel|Mac OS X|X11|CrOS|Linux x86_64/i.test(
      signals
    );
  const device = isTablet
    ? "tablet"
    : isMobile
      ? "mobile"
      : isDesktop
        ? "desktop"
        : undefined;
  const os = isIOS
    ? "iOS"
    : isAndroid
      ? "Android"
      : /Windows|Win32|Win64/i.test(signals)
        ? "Windows"
        : /Macintosh|MacIntel|Mac OS X/i.test(signals)
          ? "macOS"
          : undefined;
  const browser = /Edg(?:e|A|iOS)?\//i.test(ua)
    ? "Edge"
    : /OPR\/|OPiOS\/|Opera|SamsungBrowser\/|UCBrowser\/|Vivaldi\/|YaBrowser\/|DuckDuckGo\/|Silk\//i.test(
          ua
        )
      ? undefined
      : /Firefox\/|FxiOS\//i.test(ua)
        ? "Firefox"
        : /Chrome\/|CriOS\//i.test(ua)
          ? "Chrome"
          : /Version\/.*Safari\//i.test(ua) && !isAndroid
            ? "Safari"
            : undefined;

  return mergeTransactionMetadata(
    Object.entries({
      website_domain: facts.hostname,
      platform_channel: device
        ? device === "desktop"
          ? "web"
          : "mobile_web"
        : undefined,
      device_category: device,
      operating_system: os,
      browser,
    }).map(([Key, Value]) => ({ Key, Value }))
  );
}

function readFact(read) {
  try {
    return read();
  } catch {
    return undefined;
  }
}

/** Read each fact independently so one unavailable property does not lose the others. */
export function collectTransactionMetadata() {
  try {
    if (typeof window === "undefined") return undefined;
    return buildTransactionMetadata({
      hostname: readFact(() => window.location.hostname),
      userAgent: readFact(() => navigator.userAgent),
      platform: readFact(() => navigator.platform),
      maxTouchPoints: readFact(() => navigator.maxTouchPoints),
    });
  } catch {
    // Optional metadata must never prevent a purchase.
    return undefined;
  }
}

/** Exact keys, last valid value wins. Fresh collection belongs after caller entries. */
export function mergeTransactionMetadata(existing, collected) {
  const entries = new Map();
  for (const input of [existing, collected]) {
    if (!Array.isArray(input)) continue;
    try {
      for (const entry of input) {
        try {
          if (!entry || typeof entry !== "object" || Array.isArray(entry))
            continue;
          const { Key, Value } = entry;
          if (
            typeof Key === "string" &&
            Key.trim() &&
            typeof Value === "string" &&
            Value.trim()
          ) {
            entries.set(Key, Value);
          }
        } catch {
          // Ignore a malformed entry, including unreadable optional properties.
        }
      }
    } catch {
      // Keep usable entries if an optional input cannot be read.
    }
  }
  return entries.size
    ? Array.from(entries, ([Key, Value]) => ({ Key, Value }))
    : undefined;
}
